import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ──────────────────────────────────────────────────────────────────
// Capture the browser's PWA install prompt as early as possible.
// The `beforeinstallprompt` event can fire BEFORE React mounts,
// and it's a one-shot event — if no listener catches it in time,
// it's lost for the whole session. We stash it on window so the
// PWAInstallPrompt component can consume it whenever it mounts.
// ──────────────────────────────────────────────────────────────────
window.__pwaDeferredPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  window.__pwaDeferredPrompt = e;
  window.dispatchEvent(new Event('pwa:prompt-ready'));
});
window.addEventListener('appinstalled', () => {
  window.__pwaDeferredPrompt = null;
  window.__pwaInstalled = true;
  window.dispatchEvent(new Event('pwa:installed'));
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Service Worker Registration with SAFE update strategy
// Ensures users get latest version WITHOUT causing reload loops.
if ('serviceWorker' in navigator) {
  // Track if we're already refreshing to prevent loops within this page lifecycle
  let isRefreshing = false;

  // Reload-loop guard: even if controllerchange fires repeatedly across
  // page reloads, never auto-reload more than once every 30 seconds.
  // Without this, a misbehaving SW (e.g. one with a non-deterministic version
  // string) can put the app into an infinite splash → reload → splash loop.
  const RELOAD_COOLDOWN_MS = 30 * 1000;
  const tooSoonToReload = () => {
    try {
      const last = parseInt(sessionStorage.getItem('sw_last_reload_at') || '0', 10);
      return last && (Date.now() - last) < RELOAD_COOLDOWN_MS;
    } catch (_) {
      return false;
    }
  };
  const markReload = () => {
    try { sessionStorage.setItem('sw_last_reload_at', String(Date.now())); } catch (_) { /* ignore */ }
  };

  // Listen for controller changes BEFORE registration
  // This catches when a new SW takes control
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (isRefreshing) return;
    if (tooSoonToReload()) {
      console.warn('[App] SW controllerchange — skipping reload (cooldown active to prevent loop)');
      return;
    }
    isRefreshing = true;
    markReload();
    console.log('[App] New service worker activated, reloading for fresh content...');
    window.location.reload();
  });
  
  // Listen for messages from the service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    console.log('[App] Message from SW:', event.data);
    
    if (event.data && event.data.type === 'SW_ACTIVATED') {
      console.log('[App] SW activated with version:', event.data.version);
      // The controllerchange event will handle the reload
    }
  });
  
  // Register service worker
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        updateViaCache: 'none' // Always fetch fresh SW script
      });

      console.log('[App] SW registered successfully, scope:', registration.scope);

      // Surfaces the "Update available" UI by dispatching a window event that
      // <App> listens for. We pass the registration so the click handler can
      // call registration.waiting.postMessage({type:'SKIP_WAITING'}).
      const notifyUpdateReady = () => {
        try {
          window.dispatchEvent(new CustomEvent('sw-update-ready', {
            detail: { registration },
          }));
        } catch (e) {
          console.warn('[App] Failed to dispatch sw-update-ready:', e);
        }
      };

      // CRITICAL: if a new SW was already installed (from a previous visit)
      // and is just waiting for everyone to close the tab, surface the
      // banner right away so the user can update with one tap.
      if (registration.waiting && navigator.serviceWorker.controller) {
        console.log('[App] SW already waiting on page load — showing update banner');
        notifyUpdateReady();
      }

      // Handle the update lifecycle for SWs installed AFTER this page loaded.
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        console.log('[App] New SW detected, state:', newWorker?.state);

        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            console.log('[App] SW state changed to:', newWorker.state);

            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // A previous SW is still controlling the page. The new SW is
                // ready but waiting — show the banner instead of auto-reloading.
                console.log('[App] New SW installed — surfacing update banner');
                notifyUpdateReady();
              } else {
                // First-ever SW install (no controller yet). Nothing to do.
                console.log('[App] First SW installation complete');
              }
            }
          });
        }
      });

      // Check for updates frequently
      // Immediate check
      registration.update().catch(err => console.log('[App] Update check failed:', err));

      // Check every 2 minutes for updates
      setInterval(() => {
        registration.update().catch(err => console.log('[App] Periodic update check failed:', err));
      }, 2 * 60 * 1000);

      // Also check on visibility change (when user returns to tab)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          console.log('[App] Tab became visible, checking for SW updates');
          registration.update().catch(err => console.log('[App] Visibility update check failed:', err));

          // If a waiting SW slipped through, surface the banner.
          if (registration.waiting && navigator.serviceWorker.controller) {
            notifyUpdateReady();
          }
        }
      });

    } catch (error) {
      console.error('[App] SW registration failed:', error);
    }
  });
}

